/**
 * Build-time metadata for the deployed frontend bundle.
 *
 * The values are injected at `vite build` time via `import.meta.env.VITE_GIT_*`
 * (see `packages/frontend/vite.config.ts` → `define`). At runtime they are read
 * through the guarded `viteEnv` reader in `shared/utils/constants.ts`, which is
 * safe in BOTH the Vite browser build (real values) and the plain Node/tsx
 * prerender (falls back to the sentinels below).
 *
 * `resolveBuildInfo` is a pure function so the fallback contract can be unit
 * tested without depending on the ambient `import.meta.env`.
 */

/** Shape of the frontend build metadata surfaced to the version indicator. */
export interface BuildInfo {
  /** Semver app version from package.json (fallback: '0.1.0'). */
  readonly version: string
  /** Full 40-char git sha of the build (fallback: 'dev'). */
  readonly gitSha: string
  /** Short 7-char git sha of the build (fallback: 'dev'). */
  readonly gitShaShort: string
  /** Git branch the build was cut from (fallback: 'local'). */
  readonly gitBranch: string
  /** ISO-8601 build timestamp (fallback: '' — empty when unknown). */
  readonly buildTime: string
}

/**
 * Build the frontend `BuildInfo` from a Vite-injected env bag, degrading every
 * field to a stable sentinel when its key is absent. Never returns `undefined`
 * fields so downstream drift/label logic can rely on plain string comparisons.
 */
export function resolveBuildInfo(env: Record<string, string | undefined>): BuildInfo {
  return {
    version: env.VITE_APP_VERSION ?? '0.1.0',
    gitSha: env.VITE_GIT_SHA ?? 'dev',
    gitShaShort: env.VITE_GIT_SHA_SHORT ?? 'dev',
    gitBranch: env.VITE_GIT_BRANCH ?? 'local',
    buildTime: env.VITE_BUILD_TIME ?? '',
  }
}
